import { resolveDialogCloseParentElementMeta } from '@/common/utils/resolveDialogCloseParentStoreKey'
import {
  findRegisteredElementByName,
  findRegisteredElementByNameLoose,
  type VisibilityCapable,
} from '@/common/utils/resolveRegistryVisibility'

export type NameBasedElementVisibilityTarget = {
  elementId: string | number
  registryKey?: string
  entry?: VisibilityCapable
  source: 'registry' | 'registryLoose' | 'elementsByKey' | 'formMeta'
}

export type ResolveNameBasedElementVisibilityInput = {
  name: string
  formId?: string | number | null
  elements?: Record<string, VisibilityCapable | undefined>
  elementsByKey?: Record<string, VisibilityCapable | undefined>
  pageData?: Record<string, unknown> | null
}

function readElementId(entry: VisibilityCapable | undefined): string | number | null {
  const raw = entry?.elementId
  if (raw == null || raw === '') return null
  const asNumber = Number(raw)
  if (Number.isFinite(asNumber) && asNumber > 0) return asNumber
  const text = String(raw).trim()
  return text || null
}

/**
 * Resolve platform elementId for en.setVisible("element", name, visible)
 * when live registry may be empty (reloadForm / remount race) or keyed as name#id.
 */
export function resolveNameBasedElementVisibilityTarget(
  input: ResolveNameBasedElementVisibilityInput,
): NameBasedElementVisibilityTarget | null {
  const name = String(input.name ?? '').trim()
  if (!name) return null

  const formId =
    input.formId != null && String(input.formId).trim() !== ''
      ? String(input.formId).trim()
      : undefined
  const elements = input.elements ?? {}

  const byName = findRegisteredElementByName(elements, name, formId)
  if (byName?.entry) {
    const elementId = readElementId(byName.entry)
    if (elementId != null) {
      return {
        elementId,
        registryKey: byName.registryKey,
        entry: byName.entry,
        source: 'registry',
      }
    }
  }

  const loose = findRegisteredElementByNameLoose(elements, name, formId)
    ?? findRegisteredElementByNameLoose(elements, name, undefined)
  if (loose?.entry) {
    const elementId = readElementId(loose.entry)
    if (elementId != null) {
      return {
        elementId,
        registryKey: loose.registryKey,
        entry: loose.entry,
        source: 'registryLoose',
      }
    }
  }

  const byKeyPool = input.elementsByKey
  if (byKeyPool) {
    const byKey =
      findRegisteredElementByNameLoose(byKeyPool, name, formId)
      ?? findRegisteredElementByNameLoose(byKeyPool, name, undefined)
    if (byKey?.entry) {
      const elementId = readElementId(byKey.entry)
      if (elementId != null) {
        return {
          elementId,
          registryKey: byKey.registryKey,
          entry: byKey.entry,
          source: 'elementsByKey',
        }
      }
    }
  }

  if (!formId || !input.pageData) return null

  const meta = resolveDialogCloseParentElementMeta(input.pageData, formId, name)
  const metaId = meta?.elementId
  if (metaId == null || metaId === '') return null

  const elementId = readElementId({ elementId: metaId })
  if (elementId == null) return null

  return {
    elementId,
    source: 'formMeta',
  }
}
